import './applicantEditPersonalPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { red } from '@mui/material/colors';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { PhoneNumberDisabledInput } from '../../../components/PhoneNumberDisabledInput/PhoneNumberDisabledInput';
import { nameValidator } from '../../../components/utils/inputValidators';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    clearUserUpdate,
    computed: {
      userFirstName,
      userLastName,
      userPhoneNumber,
      userEmailAddress,
    },
    isUpdatingUser,
    setUserFirstName,
    setUserLastName,
    userUpdate,
    userUpdateSuccess,
    userUpdateFailedMessage,
  } = useUserStore();

  const {
    computed: { reviewEditPathname, reviewEditProgress },
  } = useApplicantStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      firstNameValue: userFirstName,
      lastNameValue: userLastName,
    },
    delayError: 200,
    mode: 'onBlur',
    shouldFocusError: false,
  });

  const firstNameValidation = {
    required: t('validation.firstName.required'),
    minLength: {
      value: 2,
      message: t('validation.firstName.minLength', { min: 2 }),
    },
    pattern: {
      value: nameValidator,
      message: t('validation.firstName.invalid'),
    },
  };

  const lastNameValidation = {
    required: t('validation.lastName.required'),
    minLength: {
      value: 2,
      message: t('validation.lastName.minLength', { min: 2 }),
    },
    pattern: {
      value: nameValidator,
      message: t('validation.lastName.invalid'),
    },
  };

  const finishEditing = (): void => {
    navigate(reviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { firstNameValue, lastNameValue } = values;

    if (!firstNameValue || !lastNameValue) {
      return;
    }

    setUserFirstName(firstNameValue);
    setUserLastName(lastNameValue);

    if (isDirty) {
      await userUpdate();
    } else {
      finishEditing();
    }
  };

  useEffect(
    (): (() => void) => (): void => {
      clearUserUpdate();
    },
    [],
  );

  useEffect((): void => {
    if (userUpdateSuccess) {
      finishEditing();
    }
  }, [userUpdateSuccess]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-edit-personal-page" sx={theme.pageLayout}>
        <Header
          progress={reviewEditProgress}
          showDrawerToggle
          onBackClick={finishEditing}
        />

        <main>
          <form
            data-testid={locator.pages.applicantEditPersonal.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantEditPersonal.form.h1}>
                {t('ApplicantEditPersonalPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantEditPersonal.form.h2}>
                {t('ApplicantEditPersonalPage.subtitle')}
              </h2>

              {!isEmpty(userUpdateFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantEditPersonal.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {userUpdateFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-inputs">
                  <div className="input-half">
                    <TextField
                      {...register('firstNameValue', firstNameValidation)}
                      error={!isEmpty(errors.firstNameValue)}
                      label={t('ApplicantEditPersonalPage.firstName')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.applicantEditPersonal.form
                              .firstNameInput,
                        },
                      }}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.firstNameValue) && (
                      <span
                        data-testid={
                          locator.pages.applicantEditPersonal.form
                            .firstNameInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.firstNameValue.message)}
                      </span>
                    )}
                  </div>

                  <div className="input-half">
                    <TextField
                      {...register('lastNameValue', lastNameValidation)}
                      error={!isEmpty(errors.lastNameValue)}
                      label={t('ApplicantEditPersonalPage.lastName')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.applicantEditPersonal.form
                              .lastNameInput,
                        },
                      }}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.lastNameValue) && (
                      <span
                        data-testid={
                          locator.pages.applicantEditPersonal.form
                            .lastNameInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.lastNameValue.message)}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <PhoneNumberDisabledInput
                    inputLabelKey="ApplicantEditPersonalPage.phone"
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.applicantEditPersonal.form
                            .phoneInputDisabled,
                      },
                    }}
                    value={userPhoneNumber}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    label={t('ApplicantEditPersonalPage.email')}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.applicantEditPersonal.form
                            .emailAddressInput,
                      },
                    }}
                    style={{ pointerEvents: 'none' }}
                    value={userEmailAddress}
                    variant="outlined"
                    disabled
                    fullWidth
                  />
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <Button
                    color="secondary"
                    data-testid={
                      locator.pages.applicantEditPersonal.form.cancelButton
                    }
                    loadingPosition="start"
                    startIcon={<></>}
                    type="button"
                    variant="contained"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('ApplicantEditPersonalPage.cancel')}
                  </Button>

                  <Button
                    color="primary"
                    data-testid={
                      locator.pages.applicantEditPersonal.form.submitButton
                    }
                    disabled={!isValid}
                    loading={isUpdatingUser}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('ApplicantEditPersonalPage.save')}
                  </Button>
                </div>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantEditPersonal.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantEditPersonalPage';
